/**
 * Utility functions for handling date calculations and overdue book rentals
 */

/**
 * Calculate the number of days a book is overdue
 * @param endDate - The rental end date as ISO string
 * @returns Number of days overdue, or null if not overdue
 */
export const calculateOverdueDays = (endDate: string): number | null => {
  const today = new Date();
  const end = new Date(endDate);
  
  // Set time to start of day for accurate day comparison
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  const diffTime = today.getTime() - end.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays > 0 ? diffDays : null;
};

/**
 * Check if a book rental is overdue
 * @param endDate - The rental end date as ISO string
 * @returns True if the book is overdue
 */
export const isOverdue = (endDate: string): boolean => {
  return calculateOverdueDays(endDate) !== null;
};

/**
 * Format overdue information for display
 * @param endDate - The rental end date as ISO string
 * @returns Formatted string with overdue information
 */
export const formatOverdueInfo = (endDate: string): string => {
  const overdueDays = calculateOverdueDays(endDate);
  
  if (overdueDays === null) {
    // Not overdue, format as normal rental
    const date = new Date(endDate);
    return `Loué jusqu'au ${date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short'
    })}`;
  }
  
  // Overdue, show expected return date and days overdue
  const date = new Date(endDate);
  const formattedDate = date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short'
  });
  
  return `Retour attendu : ${formattedDate} (${overdueDays} jour${overdueDays > 1 ? 's' : ''} de retard)`;
};

/**
 * Format rental date for display (existing function, moved here for consistency)
 * @param dateString - The date as ISO string
 * @returns Formatted date string
 */
export const formatRentalDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    month: 'short',
    day: 'numeric'
  });
};
